import { useEffect, useRef } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard, StatusPill } from '@jumpstone/ui-react';
import { PayRunSummaryCard } from './pay_run_summary_card';
import { PayRunApprovedLine } from './pay_run_approved_line';
import { PostRunStep } from './wizard_steps';
import { next_pay_run_period } from './pay_run_calculations';
import { PAY_RUN_STATUS_KEY, PAY_RUN_STATUS_PILL } from '../../lib/label_keys';
import { format_month_year } from '../../lib/format';
import type { PayRun } from '../../data/types';
import type { PayRunPostActionsController } from '../../data/wagepro_store';

/**
 * What Run payroll shows once the current run is approved (or paid/filed):
 * a clear "done" status, the totals, the post-run next steps (payslips,
 * disbursement, statutory payments, filings — the same sections as the
 * wizard's Post-run step), and the way on to the next month's pay run.
 * The full seven-step wizard stays reachable through "View pay run details".
 */
export function PayRunCompleteView({
  run,
  pay_runs,
  approver_name,
  post_actions,
  start_available,
  on_start_next,
  on_view_details,
  focus_heading,
}: {
  run: PayRun;
  /** Every pay run, so the CTA names the month the start panel will preselect. */
  pay_runs: readonly PayRun[];
  /** Resolved approver display name, or `undefined` when unknown. */
  approver_name: string | undefined;
  post_actions: PayRunPostActionsController | undefined;
  /** `false` hides the start CTA (read-only grant, or no GL accounts to start with). */
  start_available: boolean;
  on_start_next: () => void;
  on_view_details: () => void;
  /**
   * Move focus to the heading on mount. Set when the view replaces the
   * wizard after an interaction (sign-off, "Back to summary"), so keyboard
   * and screen-reader users land on the new status instead of `<body>`;
   * left unset on a plain page load so it never steals initial focus.
   */
  focus_heading: boolean;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const next_period = next_pay_run_period(pay_runs);
  const heading_ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focus_heading) heading_ref.current?.focus();
  }, [focus_heading]);
  // Literal t() calls (not a key map) so the i18n extractor and the
  // no-orphans gate both see every heading key.
  const title =
    run.status === 'paid'
      ? t('pay_run.complete_title_paid')
      : run.status === 'filed'
        ? t('pay_run.complete_title_filed')
        : t('pay_run.complete_title_approved');

  return (
    <div className="grid gap-4 items-start grid-cols-1 lg:grid-cols-[1fr_300px]">
      <div className="flex flex-col gap-4 min-w-0">
        <GlassCard
          className="rounded-2xl p-5"
          data-testid="pay-run-complete-view"
        >
          <StatusPill
            status={PAY_RUN_STATUS_PILL[run.status]}
            label={t(PAY_RUN_STATUS_KEY[run.status])}
          />
          <h2
            ref={heading_ref}
            tabIndex={-1}
            className="font-bold text-lg mt-2 outline-none"
          >
            {title}
          </h2>
          <PayRunApprovedLine
            approved_at={run.approved_at}
            approver_name={approver_name}
            className="text-[13px] text-[color:var(--ink-muted)] mt-1"
          />
          <div className="flex flex-wrap gap-2 mt-4">
            {start_available && (
              <Button variant="primary" size="sm" onClick={on_start_next}>
                {next_period
                  ? t('pay_run.start_pay_run_for', {
                      period: format_month_year(next_period, locale),
                    })
                  : t('pay_run.start_new_pay_run')}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={on_view_details}>
              {t('pay_run.view_details')}
            </Button>
          </div>
        </GlassCard>

        <GlassCard className="rounded-2xl p-5">
          <h2 className="font-bold mb-3">{t('pay_run.next_steps_title')}</h2>
          <PostRunStep run={run} post_actions={post_actions} />
        </GlassCard>
      </div>

      <PayRunSummaryCard run={run} />
    </div>
  );
}
